import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import SearchableMultiSelectModal from "../Commaon/SearchableMultiSelectModal";
import { formatAgeLimit } from "../../utils/ageLimit";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const AddPackageModal = () => {
  const navigate = useNavigate();

  // form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [report, setReport] = useState("");
  const [fasting, setFasting] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [gstSlab, setGstSlab] = useState("0");
  const [recommendedGender, setRecommendedGender] = useState("Both");
  const [sampleType, setSampleType] = useState("");
  const [testCriteria, setTestCriteria] = useState("");
  const [testInstruction, setTestInstruction] = useState("");
  const [testIds, setTestIds] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});

  
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const normalizeId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      if (value.$oid) return value.$oid;
      if (value._id) return normalizeId(value._id);
      if (value.id) return normalizeId(value.id);
      if (typeof value.toString === "function") {
        const stringValue = value.toString();
        return stringValue === "[object Object]" ? "" : stringValue;
      }
    }
    return String(value);
  };
  const getTestId = (test) => normalizeId(test?._id || test?.id || test);
  const testOptions = availableTests.map((test) => {
    const id = getTestId(test);
    return {
      value: id,
      label: test.title || test.name || id,
    };
  }).filter((option) => option.value);

  useEffect(() => {
    const mrp = Number(originalPrice);
    const selling = Number(discountedPrice);
    if (mrp > 0 && selling >= 0 && selling <= mrp) {
      setDiscountPercent(String(Math.round(((mrp - selling) / mrp) * 100)));
    } else {
      setDiscountPercent("");
    }
  }, [originalPrice, discountedPrice]);

  useEffect(() => {
    const fetchPackageOptions = async () => {
      try {
        const testsResponse = await AuthInterceptor("/admin/test?page=1&limit=500", {
          method: "GET",
        });
        setAvailableTests(getRows(testsResponse.data));
      } catch (error) {
        setAvailableTests([]);
      }
    };

    fetchPackageOptions();
  }, []);

  const validateAddForm = () => {
  let newErrors = {};

  // Title
  if (!title.trim()) {
    newErrors.title = "Package title is required";
  }

  // Description
  if (!description.trim()) {
    newErrors.description = "Description is required";
  }

  // Age Limit
  if (!ageFrom.trim() || !ageTo.trim()) {
    newErrors.ageLimit = "Age limit is required";
  }

  // Report
  if (!report.trim()) {
    newErrors.report = "Report information is required";
  }

  // Fasting
  if (!fasting.trim()) {
    newErrors.fasting = "Fasting information is required";
  }

  // Original Price
  if (!originalPrice.trim()) {
    newErrors.originalPrice = "Original price is required";
  } else if (isNaN(originalPrice) || Number(originalPrice) <= 0) {
    newErrors.originalPrice = "Original price must be a valid number";
  }

  // Discounted Price
  if (discountedPrice && (isNaN(discountedPrice) || Number(discountedPrice) <= 0)) {
    newErrors.discountedPrice = "Discounted price must be a valid number";
  }

  // Discount Percent
  if (
    discountPercent &&
    (isNaN(discountPercent) ||
      Number(discountPercent) < 0 ||
      Number(discountPercent) > 100)
  ) {
    newErrors.discountPercent = "Discount percent must be between 0 and 100";
  }

  // Recommended Gender
  if (!recommendedGender) {
    newErrors.recommendedGender = "Recommended gender is required";
  }

  // Sample Type
  if (!sampleType.trim()) {
    newErrors.sampleType = "Sample type is required";
  }

  // Test Criteria
  if (!testCriteria.trim()) {
    newErrors.testCriteria = "Test criteria is required";
  }

  // Test Instruction
  if (!testInstruction.trim()) {
    newErrors.testInstruction = "Test instruction is required";
  }

  // Test IDs
  if (!Array.isArray(testIds) || testIds.length === 0) {
    newErrors.testIds = "At least one test must be selected";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAddForm()) {
      return;
    }
    try {
      setLoading(true);

      const body = {
        title,
        description,
        ageLimit: formatAgeLimit(ageFrom, ageTo),
        report,
        fasting,
        originalPrice,
        discountedPrice,
        discountPercent,
        gstSlab,
        recommendedGender,
        sampleType,
        testCriteria,
        testInstruction,
        isActive,
        testIds,
      };

      const response = await AuthInterceptor(`/admin/package`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      const data = response.data;

      if (data?.s === 201 || data?.s === 200) {
        setSnack({
          notification: {
            message: "Package added successfully",
            type: "success",
          },
        });

        navigate("/packages");
      } else {
        throw new Error(data?.m || "Failed to add package");
      }
    } catch (error) {
      setSnack({
        notification: {
          message: error.message || "Error adding package",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}

      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="container mt-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Add Package</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Title */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
                {errors.title && (
                  <div className="text-danger mt-1">{errors.title}</div>
                )}
              </div>

              {/* Age Limit */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Age Limit</label>
                <div className="input-group">
                  <input type="number" min="0" max="150" className="form-control" placeholder="From" value={ageFrom} onChange={(e) => setAgeFrom(e.target.value)} />
                  <span className="input-group-text">to</span>
                  <input type="number" min="0" max="150" className="form-control" placeholder="To" value={ageTo} onChange={(e) => setAgeTo(e.target.value)} />
                </div>
                {errors.ageLimit && (
                  <div className="text-danger mt-1">{errors.ageLimit}</div>
                )}
              </div>

              {/* Description */}
              <div className="col-12 mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {errors.description && (
                  <div className="text-danger mt-1">{errors.description}</div>
                )}
              </div>

              {/* Report */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Report</label>
                <input
                  className="form-control"
                  value={report}
                  onChange={(e) => setReport(e.target.value)}
                />
                {errors.report && (
                  <div className="text-danger mt-1">{errors.report}</div>
                )}
              </div>

              {/* Sample Type */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Sample Type</label>
                <input
                  className="form-control"
                  value={sampleType}
                  onChange={(e) => setSampleType(e.target.value)}
                />
                {errors.sampleType && (
                  <div className="text-danger mt-1">{errors.sampleType}</div>
                )}
              </div>

              {/* Pricing */}
              <div className="col-md-6 mb-3">
                <label className="form-label">MRP (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={originalPrice}
                  onChange={(e) => setOriginalPrice(e.target.value)}
                />
                {errors.originalPrice && (
                  <div className="text-danger mt-1">
                    {errors.originalPrice}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discounted Price (₹)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountedPrice}
                  onChange={(e) => setDiscountedPrice(e.target.value)}
                />
                {errors.discountedPrice && (
                  <div className="text-danger mt-1">
                    {errors.discountedPrice}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discount Percent (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={discountPercent}
                  readOnly
                />
                {errors.discountPercent && (
                  <div className="text-danger mt-1">
                    {errors.discountPercent}
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">GST Slab</label>
                <select
                  className="form-select"
                  value={gstSlab}
                  onChange={(e) => setGstSlab(e.target.value)}
                >
                  <option value="0">GST 0%</option>
                  <option value="5">GST 5%</option>
                  <option value="18">GST 18%</option>
                </select>
              </div>

              {/* Gender */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Recommended Gender</label>
                <select
                  className="form-select"
                  value={recommendedGender}
                  onChange={(e) => setRecommendedGender(e.target.value)}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Both">Both</option>
                </select>
                {errors.recommendedGender && (
                  <div className="text-danger mt-1">
                    {errors.recommendedGender}
                  </div>
                )}
              </div>

              {/* Fasting */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Fasting</label>
                <input
                  className="form-control"
                  placeholder="e.g. Yes, 8 hrs"
                  value={fasting}
                  onChange={(e) => setFasting(e.target.value)}
                />
                {errors.fasting && (
                  <div className="text-danger mt-1">{errors.fasting}</div>
                )}
              </div>

              {/* Instructions */}
              <div className="col-12 mb-3">
                <label className="form-label">Test Instruction</label>
                <textarea
                  className="form-control"
                  value={testInstruction}
                  onChange={(e) => setTestInstruction(e.target.value)}
                />
                {errors.testInstruction && (
                  <div className="text-danger mt-1">
                    {errors.testInstruction}
                  </div>
                )}
              </div>

              {/* Note */}
              <div className="col-12 mb-3">
                <label className="form-label">Note</label>
                <textarea
                  className="form-control"
                  value={testCriteria}
                  onChange={(e) => setTestCriteria(e.target.value)}
                />
                {errors.testCriteria && (
                  <div className="text-danger mt-1">{errors.testCriteria}</div>
                )}
              </div>

              {/* Tests */}
              <div className="col-12 mb-3">
                <label className="form-label">Tests</label>
                <SearchableMultiSelectModal
                  title="Select Tests"
                  options={testOptions}
                  selectedValues={testIds}
                  onChange={setTestIds}
                  buttonLabel="Select tests"
                  emptyLabel="No tests selected"
                  disabled={loading}
                />
                {errors.testIds && (
                  <div className="text-danger mt-1">{errors.testIds}</div>
                )}
              </div>

              {/* Active */}
              <div className="col-md-6 mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  id="activeCheck"
                />
                <label className="form-check-label" htmlFor="activeCheck">
                  Active
                </label>
              </div>
            </div>

            <div className="text-end">
              <button
                className="btn btn-outline-custom"
                type="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Package"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddPackageModal;
